import React, { useMemo } from "react"

import { EqualHeightElement } from "react-equal-height/clean"
import { Product } from "../../../shared/sdk/types"
import { Text, useAdminContext } from "react-bricks/frontend"
import { formatPrice } from "../../../shared/utils/format"
import i18n from "../../../i18n/config"
import { useLocalesState } from "../../../shared/context/locales"
import { useTranslation } from "react-i18next"
import classNames from "classnames"

export interface MatrixRedesignItemPriceProps {
    row: number
    product?: Product
    realPrice?: boolean
}

const getPrice = (product: Product, language: string, realPrice?: boolean) => {
    return realPrice
        ? formatPrice(product.price.current.taxed, product.price.currency.name, language)
        : product.sku === "LV2START"
        ? formatPrice(
              product.price.current.taxed / (product.configuration.splitted?.parts || 1),
              product.price.currency.name,
              language
          )
        : product.sku === "LVSTARTNA"
        ? formatPrice(product.price.current.taxed / 3, product.price.currency.name, language)
        : product.configuration.subscription
        ? formatPrice(
              (product.price.current.taxed * (product.configuration.subscription.max_quantity - 1)) /
                  product.configuration.subscription.max_quantity,
              product.price.currency.name,
              language
          ) + "*"
        : formatPrice(product.price.current.taxed, product.price.currency.name, language)
}

const getSmallprint = (product: Product, language: string) => {
    return !["Liter", "litre"].includes(product.price.basic.unit)
        ? i18n.t("price_incl_vat")
        : i18n.t("price_per_litre_incl_vat", {
              price: formatPrice(product.price.basic.taxed, product.price.currency.name, language),
          })
}

const MatrixRedesignItemPrice = ({ row, product, realPrice }: MatrixRedesignItemPriceProps) => {
    const { language } = useLocalesState()
    const { t } = useTranslation("translations")
    const { isAdmin } = useAdminContext()

    const price = useMemo(
        () => (product ? getPrice(product, language, realPrice) : null),
        [product, language, realPrice]
    )

    const smallprint = useMemo(() => (product ? getSmallprint(product, language) : null), [product, language])

    if (!product) return null

    return (
        <EqualHeightElement name={"price-" + row}>
            <div className="mb-5 mt-7 flex items-center sm:mb-7 sm:mt-10">
                <p className="font-gibson text-lg sm:text-xl lg:text-lg xl:text-xl">{price}</p>
                {!!product.price.regular.taxed && product.price.regular.taxed !== product.price.current.taxed ? (
                    <p className="ml-3 text-base font-semibold sm:text-md lg:text-base xl:text-md">
                        {"("}
                        {t("price_instead")}{" "}
                        <del className="relative no-underline after:absolute after:left-0 after:top-[calc(50%_-_1px)] after:z-2 after:h-0.5 after:w-full after:-translate-y-1/2 after:rotate-[-14deg] after:bg-ui-red after:content-['']">
                            {formatPrice(product.price.regular.taxed, product.price.currency.name, language)}
                        </del>
                        {")"}
                    </p>
                ) : (
                    <Text
                        propName="priceAddition"
                        renderBlock={({ children }) => (
                            <p
                                className={classNames("ml-1.5 text-base sm:text-md lg:text-base xl:text-md", {
                                    "min-w-24": isAdmin,
                                })}
                            >
                                {children}
                            </p>
                        )}
                        placeholder="Lorem ipsum"
                    />
                )}
                <small className="text-box ml-3 whitespace-pre-wrap text-xs sm:ml-3.5 lg:ml-3 xl:ml-3.5">
                    {smallprint}
                </small>
            </div>
        </EqualHeightElement>
    )
}

export default MatrixRedesignItemPrice
